import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { InventorySummary } from '../../types';

interface InventorySummaryCardProps {
  summary: InventorySummary | undefined;
  isLoading?: boolean;
}

export function InventorySummaryCard({ summary, isLoading }: InventorySummaryCardProps) {
  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {[...Array(4)].map((_, i) => (
          <Card key={i}>
            <CardHeader className="pb-2">
              <div className="h-4 w-24 bg-muted rounded animate-pulse" />
            </CardHeader>
            <CardContent>
              <div className="h-8 w-16 bg-muted rounded animate-pulse" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!summary) {
    return null;
  }

  // Safely access totals with defaults for null/undefined values
  const totals = summary.totals || {};
  const totalItems = totals.total_items ?? 0;
  const uniqueSkus = totals.unique_skus ?? 0;
  const totalQuantity = totals.total_quantity ?? 0;
  const totalReserved = totals.total_reserved ?? 0;
  const totalAvailable = totals.total_available ?? 0;
  const lowStockCount = totals.low_stock_count ?? 0;

  const stats = [
    {
      title: 'Total Items',
      value: totalItems,
      description: `${uniqueSkus} unique SKUs`,
    },
    {
      title: 'Total Quantity',
      value: totalQuantity.toLocaleString(),
      description: `${totalReserved} reserved`,
    },
    {
      title: 'Available',
      value: totalAvailable.toLocaleString(),
      description: 'Ready for picking',
    },
    {
      title: 'Low Stock Alerts',
      value: lowStockCount,
      description: 'Below reorder point',
      highlight: lowStockCount > 0,
    },
  ];

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      {stats.map((stat) => (
        <Card key={stat.title}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {stat.title}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div
              className={`text-2xl font-bold ${
                stat.highlight ? 'text-orange-500' : ''
              }`}
            >
              {stat.value}
            </div>
            <p className="text-xs text-muted-foreground mt-1">{stat.description}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
